import type { GridApi, GridRowSelectionModel } from '@repo/go-table';
import { action, makeAutoObservable } from 'mobx';
import type { MutableRefObject } from 'react';

import { AutoAnomaliesDisplayManager } from './AutoAnomaliesDisplayManager';
import type { AutoAnomaliesService } from './AutoAnomaliesService';
import type { TAutoAnomalyRowData } from './types';

export class AutoAnomaliesTableStore {
  tableApi: MutableRefObject<GridApi>;

  displayManager: AutoAnomaliesDisplayManager | null = null;
  selectedRows: GridRowSelectionModel = [];
  isTableSettingsOpen = false;
  contextColumnsHidden = false;
  isSelectionModeActive = false;
  isMainInitialized = false;

  constructor(
    tableApi: MutableRefObject<GridApi>,
    private readonly autoAnomaliesService: AutoAnomaliesService,
  ) {
    this.tableApi = tableApi;
    makeAutoObservable(this, {
      tableApi: false,
      addAnomalies: action.bound,
      updateAnomalies: action.bound,
      deleteAnomalies: action.bound,
    });
  }

  get isInitialized(): boolean {
    return this.isMainInitialized && this.displayManager != null;
  }

  addAnomalies(rows: TAutoAnomalyRowData[]): void {
    if (!this.tableApi || !rows.length) return;
    this.tableApi.current.updateRows(rows);
  }

  updateAnomalies(rows: TAutoAnomalyRowData[]): void {
    if (!this.tableApi || !rows.length) return;
    this.tableApi.current.updateRows(rows);
  }

  deleteAnomalies(ids: string[]): void {
    if (!this.tableApi || !ids.length) return;
    this.tableApi.current.updateRows(ids.map((id) => ({ id, _action: 'delete' })));
  }

  loadDisplayManager() {
    this.displayManager = new AutoAnomaliesDisplayManager(this.autoAnomaliesService.settingsStore);
    this.contextColumnsHidden = this.displayManager.contextColumnsHidden;
  }

  setSelectedRows(model: GridRowSelectionModel): void {
    this.selectedRows = model;
    this.tableApi.current.setRowSelectionModel(model);
  }

  setIsTableSettingsOpen(is: boolean): void {
    this.isTableSettingsOpen = is;
  }

  toggleContextColumnsHidden(): void {
    const contextColumnsHidden = !this.contextColumnsHidden;
    this.contextColumnsHidden = contextColumnsHidden;
    this.displayManager?.onContextColumnsHiddenChange(contextColumnsHidden);
  }

  toggleSelectionMode(): void {
    this.isSelectionModeActive = !this.isSelectionModeActive;
    if (!this.isSelectionModeActive) {
      this.selectedRows = [];
      this.tableApi.current.setRowSelectionModel([]);
    }
  }

  init() {
    this.loadDisplayManager();

    const newDisposer = this.autoAnomaliesService.eventEmitter.addEventListener('newAutoAnomalies', this.addAnomalies);
    const updateDisposer = this.autoAnomaliesService.eventEmitter.addEventListener(
      'updateAutoAnomalies',
      this.updateAnomalies,
    );
    const deleteDisposer = this.autoAnomaliesService.eventEmitter.addEventListener(
      'deleteAutoAnomalies',
      this.deleteAnomalies,
    );

    this.isMainInitialized = true;
    return () => {
      this.isMainInitialized = false;
      this.displayManager = null;
      newDisposer();
      updateDisposer();
      deleteDisposer();
    };
  }
}
